import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import client, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { PageHeader, Card, Btn, Input, Select, Empty } from "@/components/kit";
import { PillarBalance, PillarSelect } from "@/components/Pillars";
import { PILLARS } from "@/lib/constants";
import { Lightbulb, Plus, Trash2, Sparkles, Loader2, CheckCircle2, ArrowRight, Check, X, CalendarHeart } from "lucide-react";

const FILTERS = [["", "Todas"], ["NEW", "Novas"], ["USED", "Já usadas"]];
const SOURCE = { omni: "Omni-canal", plano: "Planejamento", ia: "Sugestão IA", semanal: "Sugestão semanal", manual: "Manual" };
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit" }) : "");

function IdeaCard({ i, brandName, onUse, onRemove, onPillar }) {
  const used = i.status === "USED";
  return (
    <Card className="flex items-start gap-3" data-testid={`idea-${i.id}`}>
      {used ? <CheckCircle2 size={18} className="text-emerald-500 mt-0.5 shrink-0" /> : <Lightbulb size={18} className="text-amber-500 mt-0.5 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700">{i.text}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-1.5 text-[11px]">
          <span data-testid={`idea-status-${i.id}`} className={`rounded-full px-2 py-0.5 font-medium ${used ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
            {used ? `Usada em ${fmt(i.used_at)}` : "Nova"}
          </span>
          <PillarSelect value={i.pillar} onChange={(v) => onPillar(i, v)} testid={`idea-pillar-${i.id}`} />
          {brandName && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{brandName}</span>}
          <span className="text-slate-400">{SOURCE[i.source] || "Manual"}{i.channels?.length ? ` · ${i.channels.join(", ")}` : ""}</span>
        </div>
      </div>
      {!used && i.brand_id && (
        <button onClick={() => onUse(i)} title="Criar campanha com este tema" data-testid={`idea-use-${i.id}`}
          className="text-[#3457d5] hover:text-indigo-800 text-xs font-semibold flex items-center gap-1 shrink-0">Usar <ArrowRight size={13} /></button>
      )}
      <button onClick={() => onRemove(i.id)} className="text-slate-300 hover:text-rose-500" data-testid={`idea-delete-${i.id}`}><Trash2 size={16} /></button>
    </Card>
  );
}

function WeeklySuggestions({ items, brandName, onApprove, onDiscard }) {
  if (!items.length) return null;
  return (
    <div className="mb-6 rounded-2xl border border-indigo-100 bg-indigo-50/50 p-4" data-testid="weekly-suggestions">
      <p className="mb-3 flex items-center gap-2 text-sm font-bold text-indigo-800"><CalendarHeart size={16} /> Sugestões da semana ({items.length}) · aprove para usar</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {items.map((i) => (
          <div key={i.id} className="flex items-center gap-2 rounded-xl bg-white border border-indigo-100 px-3 py-2" data-testid={`weekly-${i.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-slate-700">{i.text}</p>
              <p className="text-[11px] text-slate-400">{[i.pillar, brandName(i.brand_id)].filter(Boolean).join(" · ")}</p>
            </div>
            <button onClick={() => onApprove(i)} title="Aprovar" data-testid={`weekly-approve-${i.id}`} className="rounded-lg bg-emerald-500 p-1.5 text-white hover:bg-emerald-600"><Check size={14} /></button>
            <button onClick={() => onDiscard(i.id)} title="Descartar" data-testid={`weekly-discard-${i.id}`} className="rounded-lg bg-rose-500 p-1.5 text-white hover:bg-rose-600"><X size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Ideias() {
  const { activeOrgId } = useAuth();
  const navigate = useNavigate();
  const [ideas, setIdeas] = useState([]);
  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [status, setStatus] = useState("");
  const [pillar, setPillar] = useState("");
  const [text, setText] = useState("");
  const [newPillar, setNewPillar] = useState("");
  const [suggesting, setSuggesting] = useState(false);

  const load = () => { if (activeOrgId) client.get("/ideas", { params: brandId ? { brand_id: brandId } : {} }).then((r) => setIdeas(r.data)); };
  useEffect(() => { if (activeOrgId) client.get("/brands").then((r) => setBrands(r.data)); }, [activeOrgId]);
  useEffect(load, [activeOrgId, brandId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!text.trim()) return;
    if (!brandId) return toast.error("Selecione a marca da ideia");
    try { await client.post("/ideas", { text, brand_id: brandId, pillar: newPillar || null }); setText(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await client.delete(`/ideas/${id}`); load(); };
  const patch = async (i, body) => {
    try { const { data } = await client.patch(`/ideas/${i.id}`, body); setIdeas((prev) => prev.map((x) => (x.id === i.id ? data : x))); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const suggest = async () => {
    if (!brandId) return toast.error("Selecione uma marca para a IA sugerir temas");
    setSuggesting(true);
    try {
      const { data } = await client.post("/ideas/suggest", { brand_id: brandId, count: 8 });
      toast.success(`${data.created.length} temas novos sugeridos, equilibrando os pilares`);
      setStatus("NEW"); setPillar(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSuggesting(false); }
  };
  const use = (i) => navigate(`/app/campanhas?brand=${i.brand_id}&theme=${encodeURIComponent(i.text)}${i.pillar ? `&pillar=${encodeURIComponent(i.pillar)}` : ""}`);

  const brandName = (id) => brands.find((b) => b.id === id)?.name;
  const weekly = ideas.filter((i) => i.status === "SUGGESTED");
  const regular = ideas.filter((i) => i.status !== "SUGGESTED");
  const visible = regular.filter((i) => (!status || (i.status || "NEW") === status) && (!pillar || i.pillar === pillar));
  const usedCount = regular.filter((i) => i.status === "USED").length;

  return (
    <div>
      <PageHeader title="Caixa de Ideias" subtitle="Temas usados nas campanhas ficam salvos aqui automaticamente, organizados por pilar, para a IA não repetir assuntos."
        actions={<Btn data-testid="ideas-suggest-btn" onClick={suggest} disabled={suggesting}>
          {suggesting ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} {suggesting ? "Sugerindo..." : "Sugerir temas com IA"}
        </Btn>} />

      <WeeklySuggestions items={weekly} brandName={brandName} onApprove={(i) => patch(i, { status: "NEW" })} onDiscard={remove} />

      <Card className="mb-4">
        <div className="flex flex-col lg:flex-row gap-3">
          <Select data-testid="ideas-brand-filter" value={brandId} onChange={(e) => setBrandId(e.target.value)} className="lg:w-64">
            <option value="">Todas as marcas</option>
            {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
          <Input data-testid="idea-input" className="flex-1" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Ex.: falar sobre prevenção de recaída após 6 meses" />
          <Select data-testid="idea-new-pillar" value={newPillar} onChange={(e) => setNewPillar(e.target.value)} className="lg:w-48">
            <option value="">Pilar (opcional)</option>
            {PILLARS.map((p) => <option key={p} value={p}>{p}</option>)}
          </Select>
          <Btn data-testid="idea-add-btn" onClick={add}><Plus size={16} /> Salvar</Btn>
        </div>
      </Card>

      <PillarBalance ideas={regular} active={pillar} onPick={setPillar} />

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="ideas-status-filter">
        {FILTERS.map(([v, l]) => (
          <button key={v || "all"} data-testid={`ideas-filter-${v || "all"}`} onClick={() => setStatus(v)}
            className={`rounded-full px-3 py-1.5 text-sm border transition-colors ${status === v ? "pf-gradient text-white border-transparent" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
        ))}
        {pillar && <span className="rounded-full bg-slate-800 px-3 py-1 text-xs text-white" data-testid="ideas-pillar-active">Pilar: {pillar}</span>}
        <span className="text-xs text-slate-400 ml-2" data-testid="ideas-count">{regular.length} ideias · {usedCount} já usadas</span>
      </div>

      {visible.length ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {visible.map((i) => <IdeaCard key={i.id} i={i} brandName={!brandId && brandName(i.brand_id)} onUse={use} onRemove={remove} onPillar={(x, v) => patch(x, { pillar: v })} />)}
        </div>
      ) : <Empty>Nenhuma ideia aqui ainda. Gere uma campanha ou clique em “Sugerir temas com IA”.</Empty>}
    </div>
  );
}
